'use client';

import { useCallback, useRef, useState } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  InfiniteLoader,
  type MasonryCellProps,
  Masonry as VirtualizedMasonry,
  WindowScroller,
} from 'react-virtualized';
import { Loader } from '@/app/components/ui/loader';
import { useFetch, useMasonry } from '@/app/hooks';
import type { Activity } from '@/app/types';
import { createPositioner } from '@/app/utils/client';
import { activitiesActivitityFeed } from '../apis';
import { ActivityCard } from './_components/ActivityCard';

const PAGE_SIZE = 12;
const SCROLL_CONTAINER_ID = 'scrollableDiv';
const COLUMNS_PRESET = 'fewer' as const;

export const ActivityPageClient = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [masonryKey, setMasonryKey] = useState(0);

  const pageTokenRef = useRef<string>('');

  const { isMounted, scrollElement, masonryRef, cellPositioner, onResize, cache } = useMasonry({
    scrollElementId: SCROLL_CONTAINER_ID,
    columnsPreset: COLUMNS_PRESET,
  });

  const { loading, run: fetchActivities } = useFetch(
    () =>
      activitiesActivitityFeed({
        query: {
          'pager.pagesz': PAGE_SIZE,
          'pager.page_token': pageTokenRef.current || undefined,
        },
      }),
    {
      onSuccess: ({ data }) => {
        if (data) {
          const newActivities = data.activities || [];
          const isFirstPage = !pageTokenRef.current;
          if (isFirstPage) {
            cache.current.clearAll();
            cellPositioner.current = null;
            setMasonryKey((k) => k + 1);
            setActivities(newActivities);
          } else {
            setActivities((prev) => [...prev, ...newActivities]);
          }
          pageTokenRef.current = data.next_page_token || '';
          setHasMore(!!data.next_page_token);
          setIsInitialLoading(false);
        }
      },
      onError: () => {
        setIsInitialLoading(false);
      },
    },
  );

  const handleLoadMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchActivities();
    }
  }, [loading, hasMore, fetchActivities]);

  const isRowLoaded = useCallback(
    ({ index }: { index: number }) => {
      return Boolean(activities[index]);
    },
    [activities],
  );

  const cellRenderer = useCallback(
    ({ index, key, parent, style: cellStyle }: MasonryCellProps) => {
      const activity = activities[index];

      if (!activity) {
        return (
          <CellMeasurer cache={cache.current} index={index} key={key} parent={parent}>
            <div style={cellStyle}>
              <div className="relative w-full">
                <div className="aspect-video w-full animate-pulse rounded-8 bg-gray-300/20" />
                <div className="mt-3 h-5 w-3/4 animate-pulse rounded bg-gray-300/20" />
                <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-gray-300/20" />
              </div>
            </div>
          </CellMeasurer>
        );
      }

      return (
        <CellMeasurer cache={cache.current} index={index} key={key} parent={parent}>
          <div style={cellStyle}>
            <ActivityCard activity={activity} />
          </div>
        </CellMeasurer>
      );
    },
    [activities],
  );

  if (!isMounted || !scrollElement || isInitialLoading) {
    return (
      <div className="flex min-h-[80vh] w-full flex-1 items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <WindowScroller scrollElement={scrollElement}>
      {({ height, scrollTop }) => (
        <InfiniteLoader
          isRowLoaded={isRowLoaded}
          loadMoreRows={handleLoadMore}
          rowCount={hasMore ? activities.length + 1 : activities.length}
          threshold={3}
        >
          {({ onRowsRendered }) => (
            <AutoSizer disableHeight onResize={onResize}>
              {({ width }) => {
                if (!cellPositioner.current) {
                  cellPositioner.current = createPositioner({
                    width,
                    cache: cache.current,
                    columnsPreset: COLUMNS_PRESET,
                  });
                }
                return (
                  <VirtualizedMasonry
                    autoHeight
                    ref={masonryRef}
                    cellCount={activities.length}
                    cellMeasurerCache={cache.current}
                    cellPositioner={cellPositioner.current}
                    cellRenderer={cellRenderer}
                    height={height}
                    width={width}
                    className="pb-28"
                    overscanByPixels={400}
                    scrollTop={scrollTop}
                    key={`masonry-${width}-${masonryKey}`}
                    onCellsRendered={({ stopIndex }) => {
                      onRowsRendered({
                        startIndex: 0,
                        stopIndex,
                      });
                    }}
                  />
                );
              }}
            </AutoSizer>
          )}
        </InfiniteLoader>
      )}
    </WindowScroller>
  );
};
